import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { CheckCircle, XCircle, ChevronLeft, ChevronRight, MapPin, Camera } from 'lucide-react';
import { api, SERVER_ORIGIN } from '../services/api';
import { shrinkPhoto } from '../utils/image';

/**
 * The inspector's week: a random handful of this week's mowed lawns, each
 * checked against the crew's own list. Every line is a pass unless tapped
 * to a fail; zero fails = a perfect lawn. The header keeps score against the
 * owner's rule (3 of 5 perfect → the crew's $590 bonus).
 */
const day = (d?: string | null) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';

/** `embedded` = a section of the owner's Field page: this week only, no week arrows, the checked lawns folded away. */
export default function InspectorHome({ embedded = false }: { embedded?: boolean }) {
  const qc = useQueryClient();
  const [weekStart, setWeekStart] = useState<string | undefined>(undefined);
  const { data, isLoading } = useQuery(['inspection-week', weekStart], () => api.get('/inspections/week', { params: weekStart ? { week_start: weekStart } : {} }).then(r => r.data));
  const [open, setOpen] = useState<number | null>(null);
  const refresh = () => qc.invalidateQueries(['inspection-week', weekStart]);
  if (isLoading || !data) return <p className="text-sm text-gray-500">Loading this week's lawns…</p>;
  const { week, inspections, summary, checklist, mowed_this_week } = data;
  const pending = inspections.filter((i: any) => i.status === 'pending');
  const done = inspections.filter((i: any) => i.status !== 'pending');
  return (
    <div className="space-y-4">
      {embedded ? (
        <div>
          <h1 className="text-2xl font-black uppercase tracking-tight text-gray-900">Lawns to inspect</h1>
          <p className="text-xs text-gray-500">This week · {summary.perfect} perfect of {summary.done} checked · need {summary.pass_needed} of {summary.sample_size} for the crew's ${summary.bonus}</p>
        </div>
      ) : (
      <div className="flex items-center justify-between">
        <button onClick={() => setWeekStart(data.prev_week)} className="p-2 rounded-lg border border-gray-200 text-gray-600"><ChevronLeft className="h-4 w-4" /></button>
        <div className="text-center">
          <p className="text-sm font-bold text-gray-900">Week of {day(week.week_start)}</p>
          <p className="text-[11px] text-gray-500">{week.status === 'closed' ? (week.passed ? '✓ Passed — bonus paid' : '✗ Did not pass') : week.is_current ? 'This week' : 'Open'}</p>
        </div>
        <button onClick={() => setWeekStart(data.next_week)} disabled={week.is_current} className="p-2 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
      </div>
      )}

      {/* Score against the rule */}
      {!embedded && <div className={`rounded-xl border px-4 py-3 ${summary.passing ? 'border-emerald-300 bg-emerald-50' : summary.still_possible ? 'border-brand-200 bg-brand-50' : 'border-red-200 bg-red-50'}`}>
        <p className="text-sm font-bold text-gray-900">{summary.perfect} perfect of {summary.done} checked · need {summary.pass_needed} of {summary.sample_size}</p>
        <p className="text-xs text-gray-600 mt-0.5">
          {summary.passing ? `The week passes — the crew earns the $${summary.bonus} bonus when Michael closes it.`
            : summary.still_possible ? `${summary.pass_needed - summary.perfect} more perfect lawn${summary.pass_needed - summary.perfect === 1 ? '' : 's'} and the crew gets $${summary.bonus}.`
            : `Not enough lawns left to reach ${summary.pass_needed} — no bonus this week.`}
        </p>
        <p className="text-[11px] text-gray-500 mt-1">{mowed_this_week} lawns mowed this week so far · lawns are picked at random as they get done.</p>
      </div>}

      {pending.length === 0 && done.length === 0 && (
        <div className="card p-6 text-center text-sm text-gray-500">Nothing to inspect yet — the list fills as this week's mowing is finished. Check back later today.</div>
      )}

      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">To inspect</p>
          {pending.map((i: any) => (
            <div key={i.id} className="card">
              <button onClick={() => setOpen(open === i.id ? null : i.id)} className="w-full text-left px-4 py-3">
                <p className="text-sm font-semibold text-gray-900">{i.customer_name}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{[i.address, i.city].filter(Boolean).join(', ')}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">Mowed {day(i.mowed_on)}{i.crew ? ` by ${i.crew}` : ''}</p>
              </button>
              {open === i.id && <InspectionForm inspection={i} checklist={checklist} onDone={() => { setOpen(null); refresh(); }} closed={week.status === 'closed'} />}
            </div>
          ))}
        </div>
      )}

      {done.length > 0 && (
        <details className="space-y-2" open={!embedded}>
          <summary className={`text-xs font-bold text-gray-500 uppercase tracking-wide ${embedded ? 'cursor-pointer' : 'list-none pointer-events-none'}`}>{embedded ? `${done.length} checked this week` : 'Checked'}</summary>
          {done.map((i: any) => (
            <div key={i.id} className="card px-4 py-3 flex items-start gap-3">
              {i.status === 'skipped' ? <XCircle className="h-5 w-5 text-gray-300 flex-shrink-0" /> : i.mistakes === 0 ? <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" /> : <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">{i.customer_name} <span className="font-normal text-gray-500">· {i.status === 'skipped' ? 'skipped' : i.mistakes === 0 ? 'perfect' : `${i.mistakes} mistake${i.mistakes === 1 ? '' : 's'}`}</span></p>
                <p className="text-xs text-gray-500">{[i.address, i.city].filter(Boolean).join(', ')}{i.crew ? ` · ${i.crew}` : ''}</p>
                {i.checks && i.mistakes > 0 && (
                  <ul className="mt-1 text-xs text-red-700 space-y-1">
                    {checklist.filter((c: any) => i.checks[c.key] === false).map((c: any) => (
                      <li key={c.key}>✗ {c.role}: {c.text}
                        {(i.photos || []).filter((p: any) => p.key === c.key).length > 0 && (
                          <span className="flex gap-1 mt-1">{(i.photos || []).filter((p: any) => p.key === c.key).map((p: any) => (
                            <a key={p.id} href={`${SERVER_ORIGIN}${p.url}`} target="_blank" rel="noopener noreferrer"><img src={`${SERVER_ORIGIN}${p.url}`} alt="" className="h-14 w-14 object-cover rounded border border-red-200" /></a>
                          ))}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {(i.photos || []).some((p: any) => !p.key) && (
                  <span className="flex gap-1 mt-1">{(i.photos || []).filter((p: any) => !p.key).map((p: any) => (
                    <a key={p.id} href={`${SERVER_ORIGIN}${p.url}`} target="_blank" rel="noopener noreferrer"><img src={`${SERVER_ORIGIN}${p.url}`} alt="" className="h-14 w-14 object-cover rounded border border-gray-200" /></a>
                  ))}</span>
                )}
                {i.notes && i.status !== 'skipped' && <p className="text-xs text-gray-600 mt-1">{i.notes}</p>}
              </div>
            </div>
          ))}
        </details>
      )}
      {!embedded && <p className="text-[11px] text-gray-500 text-center">Same list the crew sees on their Today screen — you are checking against exactly what they were told.</p>}
    </div>
  );
}

function InspectionForm({ inspection, checklist, onDone, closed }: { inspection: any; checklist: any[]; onDone: () => void; closed: boolean }) {
  const [checks, setChecks] = useState<Record<string, boolean>>(() => Object.fromEntries(checklist.map(c => [c.key, true])));
  const [notes, setNotes] = useState('');
  const mistakes = Object.values(checks).filter(v => v === false).length;
  const save = useMutation(() => api.post(`/inspections/${inspection.id}/result`, { checks, notes }), {
    onSuccess: (r: any) => { if (r.status !== 202) toast.success(r.data?.message || 'Saved'); onDone(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  const skip = useMutation(() => api.post(`/inspections/${inspection.id}/skip`, { reason: notes || 'could not get to it' }), {
    onSuccess: (r: any) => { if (r.status !== 202) toast.success(r.data?.message || 'Skipped'); onDone(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not skip'); },
  });
  const roles = Array.from(new Set(checklist.map(c => c.role)));
  // Photos beside a failed line: shrunk on the phone, saved straight away.
  const [photos, setPhotos] = useState<any[]>(inspection.photos || []);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  async function addPhoto(key: string, file: File | undefined) {
    if (!file) return;
    setUploadingKey(key);
    try {
      let f = file; try { f = await shrinkPhoto(file); } catch { /* as-is */ }
      const form = new FormData(); form.append('photo', f); form.append('key', key);
      const r = await api.post(`/inspections/${inspection.id}/photo`, form);
      if (r.data?.id) setPhotos(v => [...v, { id: r.data.id, key: r.data.key, url: r.data.url }]);
    } catch (e: any) { toast.error(e.response?.data?.message || 'Could not save the photo'); }
    finally { setUploadingKey(null); }
  }
  async function removePhoto(id: number) {
    try { await api.delete(`/inspections/photos/${id}`); setPhotos(v => v.filter(p => p.id !== id)); } catch { toast.error('Could not remove that'); }
  }
  return (
    <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
      <p className="text-xs text-gray-500">Tap a line to mark it a fail, then add a photo of it. Everything starts as a pass.</p>
      {roles.map(role => (
        <div key={role}>
          <p className="text-xs font-bold text-brand-900 uppercase tracking-wide mb-1">{role}</p>
          <div className="space-y-1">
            {checklist.filter(c => c.role === role).map(c => (
              <div key={c.key}>
                <button type="button" onClick={() => setChecks(v => ({ ...v, [c.key]: !v[c.key] }))} disabled={closed}
                  className={`w-full text-left flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${checks[c.key] ? 'border-emerald-200 bg-emerald-50 text-gray-800' : 'border-red-300 bg-red-50 text-red-900'}`}>
                  {checks[c.key] ? <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />}
                  <span className="leading-snug">{c.text}</span>
                </button>
                {!checks[c.key] && (
                  <div className="flex items-center gap-2 flex-wrap pl-3 py-1.5">
                    {photos.filter(p => p.key === c.key).map(p => (
                      <span key={p.id} className="relative">
                        <img src={`${SERVER_ORIGIN}${p.url}`} alt="" className="h-14 w-14 object-cover rounded border border-red-200" />
                        {!closed && <button type="button" onClick={() => removePhoto(p.id)} className="absolute -top-1.5 -right-1.5 bg-black/70 text-snow rounded-full h-4 w-4 text-[10px] leading-4">×</button>}
                      </span>
                    ))}
                    {!closed && (
                      <label className="text-xs font-medium text-red-700 border border-red-300 rounded-lg px-2 py-1.5 cursor-pointer flex items-center gap-1 bg-white">
                        <Camera className="h-3.5 w-3.5" /> {uploadingKey === c.key ? 'Saving…' : 'Photo of this'}
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => { addPhoto(c.key, e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="input text-sm w-full" placeholder="What you saw (optional)" />
      <div className="flex gap-2">
        <button onClick={() => save.mutate()} disabled={save.isLoading || closed} className={`flex-1 py-3 rounded-xl text-white font-semibold disabled:opacity-50 ${mistakes === 0 ? 'bg-emerald-600' : 'bg-red-600'}`}>
          {save.isLoading ? 'Saving…' : mistakes === 0 ? 'Perfect — save' : `Save with ${mistakes} mistake${mistakes === 1 ? '' : 's'}`}
        </button>
        <button onClick={() => { if (window.confirm('Skip this lawn and get another one?')) skip.mutate(); }} disabled={skip.isLoading || closed} className="px-4 py-3 rounded-xl border border-gray-300 text-gray-600 text-sm">Can't get to it</button>
      </div>
    </div>
  );
}
